import { useState, type ChangeEvent, type FocusEvent } from "react";
import { Input } from "@wootingkb/component-library";
import type { Country, PhoneInputProps } from "./types";
import { COUNTRIES } from "./constants";
import { normalizeInternationalPrefix } from "./utils";
import { CountrySelect } from "./CountrySelect";

export function PhoneNumberInput({
  value,
  defaultCountry = "CN",
  countries = COUNTRIES,
  renderFlag,
  onChange,
  onCountryChange,
  isDisabled,
  ...inputProps
}: PhoneInputProps) {
  const [country, setCountry] = useState<Country>(
    () => countries.find((c) => c.iso2 === defaultCountry) ?? countries[0]
  );
  const [internal, setInternal] = useState("");
  const nationalNumber = value ?? internal;

  const emit = (nextCountry: Country, nextNumber: string) => {
    const digits = nextNumber.replace(/\D/g, "");
    onChange?.({
      country: nextCountry,
      dialCode: nextCountry.dialCode,
      nationalNumber: nextNumber,
      e164: `${nextCountry.dialCode}${digits}`,
    });
  };

  const applyPhone = (nextCountry: Country, nextNumber: string) => {
    if (nextCountry.iso2 !== country.iso2) {
      setCountry(nextCountry);
      onCountryChange?.(nextCountry);
    }
    if (value === undefined) setInternal(nextNumber);
    emit(nextCountry, nextNumber);
  };

  const handleNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // While typing, only rewrite the box once the entered prefix (+XX / 00XX)
    // completes a code that maps to a selectable country — then swap + strip.
    // Otherwise leave what was typed so a prefix can still be entered; a
    // leftover, unmatched prefix is cleared on blur (see handleNumberBlur).
    const normalized = normalizeInternationalPrefix(raw, countries);
    if (normalized?.country) {
      applyPhone(normalized.country, normalized.national);
      return;
    }
    if (value === undefined) setInternal(raw);
    emit(country, raw);
  };

  const handleNumberBlur = (e: FocusEvent<HTMLInputElement>) => {
    inputProps.onBlur?.(e);
    // Clear an international escape (+ / 00) that was typed but never matched a
    // selectable country, keeping the digits and the current country.
    const normalized = normalizeInternationalPrefix(nationalNumber, countries);
    if (normalized)
      applyPhone(normalized.country ?? country, normalized.national);
  };

  const handleCountrySelect = (next: Country) => {
    setCountry(next);
    onCountryChange?.(next);
    emit(next, nationalNumber);
  };

  return (
    <Input
      {...inputProps}
      type="tel"
      inputMode="tel"
      autoComplete="tel-national"
      isDisabled={isDisabled}
      value={nationalNumber}
      onChange={handleNumberChange}
      onBlur={handleNumberBlur}
      // Kill the wrapper's default left padding so the selector sits flush.
      leftIconClassName="!pl-2.5"
      leftIcon={
        <CountrySelect
          country={country}
          countries={countries}
          disabled={isDisabled}
          renderFlag={renderFlag}
          onSelect={handleCountrySelect}
        />
      }
    />
  );
}
